import { useState } from 'react'
import { sortBy } from 'lodash'
import { toast } from 'sonner'

import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '~/components/ui/Table'
import { OrgInvitationStatus } from '~/app/Org/OrgSettings/types'
import {
  Invitation,
  useOrgInvitationsQuery
} from '~/graphql/hooks/useOrgInvitationsQuery'
import { useCancelInviteMutation } from '~/graphql/hooks/useCancelInviteQuery'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/AlertDialog'
import { LoaderCircleIcon } from 'lucide-react'
import Badge from '../ui/Badge'

interface PendingInvitesProps {
  orgId: number
}

export function PendingInvites({ orgId }: PendingInvitesProps) {
  const {
    invitations,
    refetchOrgInvitations,
    loading: invitationsLoading
  } = useOrgInvitationsQuery(orgId)
  const { cancelInvite, loading: invitationCanceling } = useCancelInviteMutation()
  const [inviteToCancel, setInviteToCancel] = useState<Invitation>()

  const pendingInvitations = sortBy(
    invitations.filter((member) => member.status === OrgInvitationStatus.PENDING),
    ['createdAt']
  )

  if (invitationsLoading || pendingInvitations.length === 0) return null

  const onCancelConfirm = async (invite: Invitation) => {
    const response = await cancelInvite(invite.id)
    if (response) {
      toast.success(`Invitation for ${invite.email} canceled.`)
      setInviteToCancel(undefined)
      await refetchOrgInvitations()
    }
  }

  const CancelInvitationDialog = () => {
    return (
      <AlertDialog open={!!inviteToCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invite for {inviteToCancel?.email}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setInviteToCancel(undefined)}>
              No, Go Back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void onCancelConfirm(inviteToCancel!)}
              disabled={invitationCanceling}
            >
              {invitationCanceling && (
                <LoaderCircleIcon className="mr-2 h-5 w-5 animate-spin" />
              )}
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  return (
    <>
      <h4 className="text-xl font-semibold">Pending Invites</h4>

      <CancelInvitationDialog />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pendingInvitations.map((invite) => (
            <TableRow key={invite.id}>
              <TableCell>
                {invite.email}{' '}
                <Badge variant="neutral" className="ml-2 font-light">
                  <p className="w-full text-center">Pending...</p>
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex justify-between">
                  <span>{invite.role}</span>{' '}
                  <button
                    onClick={() => setInviteToCancel(invite)}
                    className="h-6 px-2 rounded-full hover:bg-divider inline-flex justify-center items-center"
                  >
                    Cancel Invite
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  )
}
